import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    SportHeaderRow,
    SportHeaderRowLogo,
    SportHeaderWrapper,
    SportName,
    Loader,
    LoaderWrapper,
    SportHeaderUnifiedWrapper,
    SportHeaderFlag,
} from 'src/domains/layouts/webview/components/sportHeader/SportHeader.style';
import { CustomizedSportHeaderState } from './CustomizedSportHeaderState';
import { SportHeaderContainer } from 'src/domains/layouts/shared/SportsAndScoreboards.style';
import bgPattern from 'src/domains/layouts/shared/assets/backgrounds/sports/bgPatternHexagon.webp';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { RenderEventFilters } from 'src/domains/layouts/webview/components/sportHeader/SportHeaderFilters';
import { useCommon } from 'src/domains/common/Common';
import { TranslationsStore } from 'src/domains/layouts/state/translationsStore/TranslationsStore';
import { getNewHeaderOptions } from 'src/domains/layouts/webview/components/sports/general/config';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';

export interface SportHeaderPropsType {
    sportId: string;
    competitionId: CompetitionId | null;
    options: {
        desktopImage: string | null | undefined;
        tabletImage: string | null | undefined;
        mobileImage: string | null | undefined;
    };
    name?: string;
    isSpecialEvent?: boolean;
    isCompetitionPage: boolean;
}

export const SportHeaderStar = observer('SportHeaderStar', (props: SportHeaderPropsType) => {
    const common = useCommon();
    const appSportsBookState = AppSportsBookState.get(common);
    const unifiedComponentConfig = ConfigComponents.get(common).unifiedComponentConfig;
    const { sportId, competitionId, isSpecialEvent, isCompetitionPage } = props;
    const specialSportState = appSportsBookState.specialSportsMap.get(sportId);
    const specialExpandForSport = specialSportState.specialExpandForSport;
    const translationsStore = TranslationsStore.get(common);
    const sportName = specialExpandForSport?.label ?? translationsStore.translateSport(sportId);

    const isFootball = sportId === 'football';
    const isSpecialSport = props.isSpecialEvent;
    const {
        componentVariants: { sportsbookVariant },
        unifiedSportEventsTable,
    } = unifiedComponentConfig;

    const sportState = new CustomizedSportHeaderState(common, props);

    const sportHeaderDetails = sportState.getSportDetails;
    const renderFilters = isFootball || isSpecialSport === true;
    const competitionName = props.competitionId?.getModel()?.name;
    const title = (isCompetitionPage ? competitionName : sportHeaderDetails?.sport_label) ?? sportName;
    const bgImageBySport = getNewHeaderOptions()[sportId];
    const sportWideListState = appSportsBookState.unifiedSportEventsListState.get(sportId).get(competitionId);
    const flagCode = sportWideListState.flagCodeByCompetitionId;

    return (
        <>
            {isSpecialSport === true ? null : (
                <SportHeaderContainer
                    variant={sportsbookVariant}
                    bgImage={bgPattern}
                    isActiveSportHeaderImage={sportState.isActiveHeaderImage}
                >
                    {sportState.isLoading === true ? (
                        <LoaderWrapper variant={sportsbookVariant}>
                            <Loader />
                        </LoaderWrapper>
                    ) : unifiedSportEventsTable ? (
                        <SportHeaderUnifiedWrapper
                            bgImage={bgImageBySport}
                            data-test='sport-header-unified-wrapper'
                        >
                            <SportHeaderRow
                                hasImage={sportState.isActiveHeaderImage}
                                textColor={sportHeaderDetails?.sport_label_color}
                                variant={sportsbookVariant}
                            >
                                {flagCode ? (
                                    <SportHeaderFlag
                                        flagCode={flagCode}
                                        dataTest='sport-header-flag'
                                    />
                                ) : null}
                                <SportName hasImage={sportState.isActiveHeaderImage}>{title}</SportName>
                            </SportHeaderRow>
                        </SportHeaderUnifiedWrapper>
                    ) : (
                        <SportHeaderWrapper
                            hasImage={sportState.isActiveHeaderImage}
                            bgColor={sportHeaderDetails?.header_bg_color}
                            isCustomImgDesktop={sportState.isCustomImgDesktop}
                            isCustomImgTablet={sportState.isCustomImgTablet}
                            isCustomImgMobile={sportState.isCustomImgMobile}
                            backgroundImgDesktop={sportState.headerImgDesktop}
                            backgroundImgTablet={sportState.headerImgTablet}
                            backgroundImgMobile={sportState.headerImgMobile}
                            data-test='sport-header-wrapper'
                        >
                            <SportHeaderRow
                                hasImage={sportState.isActiveHeaderImage}
                                textColor={sportHeaderDetails?.sport_label_color}
                                variant={sportsbookVariant}
                            >
                                {sportName === 'American Football' ? <SportHeaderRowLogo /> : null}
                                <SportName hasImage={sportState.isActiveHeaderImage}>{title}</SportName>
                            </SportHeaderRow>
                        </SportHeaderWrapper>
                    )}
                </SportHeaderContainer>
            )}

            {renderFilters === true ? (
                <RenderEventFilters
                    competitionId={props.competitionId}
                    isCompetitionPage={isCompetitionPage}
                    isSpecialEvent={isSpecialEvent}
                    specialSportsState={specialSportState}
                    sportName={sportId}
                />
            ) : null}
        </>
    );
});
